import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useIdentity } from "../../auth/useIdentity";
import { canDiagnose, canEditProfile, canEditRadiusAttrs, canReadReceipts, canRecharge, canSendLoginDetails } from "../../components/nav";
import { loginDetailsAction, loginDetailsStatusLine } from "../../api/loginDetails";
import { SubscriberLoginDetailsDialog } from "./SubscriberLoginDetailsDialog";
import type { InvoicePrefSubscriber } from "../../api/invoicePrefs";
import {
  addressLine,
  daysLabel,
  daysTone,
  loginKindLabel,
  searchable,
  searchSubscribers,
  soonestDays,
  SEARCH_MIN,
  type SearchResult,
  type SubscriberMatch,
} from "../../api/subscriberSearch";
import { SubscriberDiagnoseDialog } from "./SubscriberDiagnoseDialog";
import { SubscriberProfileDialog } from "./SubscriberProfileDialog";
import { SubscriberRadiusAttrsDialog } from "./SubscriberRadiusAttrsDialog";
import { SubscriberReceiptsDialog } from "./SubscriberReceiptsDialog";
import { SubscriberRechargeDialog } from "./SubscriberRechargeDialog";

/**
 * Users › Customer lookup (owner 2026-10-18): "type the mobile number only --
 * try other index also like wild search of name or address or ONU or kyc and
 * get all the possibilities", each with plan, address, phone, KYC, expiry and
 * the days left to recharge. Was a mobile-only report that returned ids.
 */
export function CustomerLookupScreen() {
  const identity = useIdentity();
  const ns = identity.namespace ?? "";
  const [q, setQ] = useState("");
  const [typed, setTyped] = useState("");
  // Search once the typing pauses, not on every key.
  useEffect(() => {
    const t = setTimeout(() => setQ(typed.trim()), 350);
    return () => clearTimeout(t);
  }, [typed]);

  const search = useQuery({
    queryKey: ["subscribersearch", ns, q],
    enabled: Boolean(identity.token && ns && searchable(q)),
    queryFn: ({ signal }) => searchSubscribers(identity, ns, q, signal),
    retry: false,
    staleTime: 15_000,
  });

  const [rechargeFor, setRechargeFor] = useState<InvoicePrefSubscriber | null>(null);
  const [receiptsFor, setReceiptsFor] = useState<InvoicePrefSubscriber | null>(null);
  const [diagnoseFor, setDiagnoseFor] = useState<InvoicePrefSubscriber | null>(null);
  const [profileFor, setProfileFor] = useState<SubscriberMatch | null>(null);
  const [loginFor, setLoginFor] = useState<SubscriberMatch | null>(null);
  const [radiusFor, setRadiusFor] = useState<InvoicePrefSubscriber | null>(null);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <CustomerLookupView
        typed={typed}
        onType={setTyped}
        query={q}
        loading={search.isFetching}
        error={search.error ? (search.error instanceof Error ? search.error.message : String(search.error)) : null}
        result={searchable(q) ? (search.data ?? null) : null}
        mayRecharge={canRecharge(identity.roles)}
        mayReceipts={canReadReceipts(identity.roles)}
        mayDiagnose={canDiagnose(identity.roles)}
        mayEditProfile={canEditProfile(identity.roles)}
        maySendLogin={canSendLoginDetails(identity.roles)}
        mayEditRadius={canEditRadiusAttrs(identity.roles)}
        onRecharge={(m) => setRechargeFor(asSubscriber(m))}
        onReceipts={(m) => setReceiptsFor(asSubscriber(m))}
        onDiagnose={(m) => setDiagnoseFor(asSubscriber(m))}
        onEditProfile={setProfileFor}
        onSendLogin={setLoginFor}
        onEditRadius={(m) => setRadiusFor(asSubscriber(m))}
      />
      <SubscriberRechargeDialog ns={ns} subscriber={rechargeFor} onClose={() => setRechargeFor(null)} />
      <SubscriberReceiptsDialog ns={ns} subscriber={receiptsFor} onClose={() => setReceiptsFor(null)} />
      <SubscriberDiagnoseDialog ns={ns} subscriber={diagnoseFor} onClose={() => setDiagnoseFor(null)} />
      <SubscriberProfileDialog ns={ns} subscriber={profileFor} onClose={() => setProfileFor(null)} onSaved={() => void search.refetch()} />
      <SubscriberLoginDetailsDialog ns={ns} subscriber={loginFor} onClose={() => setLoginFor(null)} onSent={() => void search.refetch()} />
      <SubscriberRadiusAttrsDialog ns={ns} subscriber={radiusFor} onClose={() => setRadiusFor(null)} />
    </Box>
  );
}

/** The dialogs' subscriber shape. */
export function asSubscriber(m: SubscriberMatch): InvoicePrefSubscriber {
  return { accountid: m.accountid, username: m.username, name: m.name, mobile: m.mobile, status: m.status };
}

export interface CustomerLookupViewProps {
  typed: string;
  onType: (s: string) => void;
  query: string;
  loading: boolean;
  error: string | null;
  result: SearchResult | null;
  mayRecharge: boolean;
  /** subscriberreceipt:read -- wider than recharge: viewer reads receipts. */
  mayReceipts: boolean;
  mayDiagnose: boolean;
  /** Edit profile (radaccess subscriberprofile:write, v1.123); absent: not offered. */
  mayEditProfile?: boolean;
  onRecharge: (m: SubscriberMatch) => void;
  onReceipts: (m: SubscriberMatch) => void;
  onDiagnose: (m: SubscriberMatch) => void;
  onEditProfile?: (m: SubscriberMatch) => void;
  /** Send login details / Reset password (radaccess subscriberlogin:write, v1.125); absent: not offered. */
  maySendLogin?: boolean;
  onSendLogin?: (m: SubscriberMatch) => void;
  /** RADIUS attributes (special): Owner and superadmin only (radaccess subscriberradius, v1.216); absent: not offered. */
  mayEditRadius?: boolean;
  onEditRadius?: (m: SubscriberMatch) => void;
}

/** The screen without its data hooks, so a test can render it. */
export function CustomerLookupView(p: CustomerLookupViewProps) {
  const short = p.typed.trim() !== "" && !searchable(p.typed);
  return (
    <>
      <Box>
        <Typography variant="h5" component="h1">
          Customer lookup
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Type anything the subscriber tells you: mobile, name, email, address or PIN code, device or ONU MAC, router serial, or ID proof number.
        </Typography>
      </Box>
      <TextField
        size="small"
        label="Search"
        autoFocus
        value={p.typed}
        onChange={(e) => p.onType(e.target.value)}
        inputProps={{ inputMode: "search" }}
        helperText={short ? `Type at least ${SEARCH_MIN} characters` : p.loading ? "Searching…" : " "}
        sx={{ maxWidth: 480 }}
      />
      {p.error && <Alert severity="error">{p.error}</Alert>}
      {p.result && p.result.matches.length === 0 && !p.loading && <Alert severity="info">No subscriber on this site matches “{p.result.query}”.</Alert>}
      {p.result && p.result.matches.length > 0 && (
        <Typography variant="body2" color="text.secondary">
          {p.result.matches.length === 1 ? "1 subscriber" : `${p.result.matches.length} subscribers`} match “{p.result.query}”
          {p.result.more ? " — more matched; type more to narrow." : "."}
        </Typography>
      )}
      {p.result?.matches.map((m) => <MatchCard key={m.accountid} m={m} {...p} />)}
    </>
  );
}

const TONE_COLOR = { expired: "error", soon: "warning", ok: "success", none: "default" } as const;

function MatchCard({
  m,
  mayRecharge,
  mayReceipts,
  mayDiagnose,
  mayEditProfile,
  maySendLogin,
  mayEditRadius,
  onRecharge,
  onReceipts,
  onDiagnose,
  onEditProfile,
  onSendLogin,
  onEditRadius,
}: { m: SubscriberMatch } & CustomerLookupViewProps) {
  const login = loginDetailsAction(m);
  const disabled = m.status === "disable";
  const soonest = soonestDays(m);
  const billing = addressLine(m.billing_address);
  const ids = [
    ...(m.idprooftype || m.idproofnumber ? [`${m.idprooftype || "ID"} ${m.idproofnumber}`.trim()] : []),
    ...m.kyc.map((k) => `${k.type} ${k.number}${k.verified ? " (verified)" : ""}`),
  ];
  return (
    <Paper sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1 }}>
      <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
        <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 600 }}>
          {m.name || m.username || m.accountid}
        </Typography>
        <Typography variant="body2">{m.mobile}</Typography>
        {disabled && <Chip size="small" color="error" label="Disabled" />}
        <Chip size="small" color={TONE_COLOR[daysTone(soonest)]} label={daysLabel(soonest)} />
        <Box sx={{ flexGrow: 1 }} />
        {mayRecharge && !disabled && (
          <Button size="small" variant="contained" onClick={() => onRecharge(m)}>
            Recharge
          </Button>
        )}
        {mayReceipts && (
          <Button size="small" onClick={() => onReceipts(m)}>
            Receipts
          </Button>
        )}
        {mayEditProfile && onEditProfile && (
          <Button size="small" onClick={() => onEditProfile(m)}>
            Edit profile
          </Button>
        )}
        {maySendLogin && onSendLogin && (
          <Button size="small" disabled={login.disabled} title={login.why || undefined} onClick={() => onSendLogin(m)}>
            {login.label}
          </Button>
        )}
        {mayDiagnose && (
          <Button size="small" onClick={() => onDiagnose(m)}>
            Diagnose
          </Button>
        )}
        {mayEditRadius && onEditRadius && (
          <Button size="small" color="warning" onClick={() => onEditRadius(m)}>
            RADIUS attributes
          </Button>
        )}
      </Box>
      <Typography variant="caption" color="text.secondary">
        Found by {m.matched_on.join(", ")}
        {m.registered ? ` · subscriber since ${m.registered.slice(0, 10)}` : ""}
      </Typography>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "max-content 1fr" }, columnGap: 2, rowGap: 0.5 }}>
        {m.email && <Field label="Email" value={m.email_confirmed === undefined ? m.email : `${m.email} (${m.email_confirmed ? "confirmed" : "not confirmed"})`} />}
        {m.login_details !== undefined && <Field label="Login details" value={loginDetailsStatusLine(m)} />}
        {m.gender && <Field label="Gender" value={m.gender === "male" ? "Male" : m.gender === "female" ? "Female" : m.gender} />}
        <Field label="Billing address" value={billing || "—"} />
        <Field label="ID proof" value={ids.length ? ids.join(" · ") : "—"} />
      </Box>
      {m.logins.length > 0 ? (
        <Box sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Login</TableCell>
                <TableCell>Plan</TableCell>
                <TableCell>Expiry</TableCell>
                <TableCell>Recharge due</TableCell>
                <TableCell>Installed at</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {m.logins.map((l) => (
                <TableRow key={l.username}>
                  <TableCell>
                    <code>{l.username}</code>
                    <Typography variant="caption" display="block" color="text.secondary">
                      {loginKindLabel(l.kind)}
                    </Typography>
                  </TableCell>
                  <TableCell>{l.plan || "—"}</TableCell>
                  <TableCell>{l.expiry || "—"}</TableCell>
                  <TableCell>
                    <Chip size="small" variant="outlined" color={TONE_COLOR[daysTone(l.days_left)]} label={daysLabel(l.days_left)} />
                  </TableCell>
                  <TableCell>{addressLine(l.installation_address) || (billing ? "Billing address" : "—")}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary">
          No active login.
        </Typography>
      )}
    </Paper>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2">{value}</Typography>
    </>
  );
}
